import { Skeleton, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as SubframeCore from "@subframe/core";
import { ERROR_CODE } from "constants/constants";
import { EVENT_TYPES } from "constants/mixpanel";
import dayjs from "dayjs";
import useUser from "hooks/userHooks";
import {
  ESCALATION_REASONS,
  INBOX_DATE_FILTER_OPTIONS,
  INBOX_ESCALATION_TYPE,
  INBOX_TABS,
  SORT_BY_OPTIONS,
} from "modules/Retention/constants/constants";
import {
  fetchEmployee,
  fetchEmployeeHistory,
  fetchEscalations,
  fetchInboxSummary,
} from "modules/Retention/services/retention";
import {
  initializeSentiment,
  renderSentimentBadgeWithScore,
} from "modules/Retention/utils/sentiment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mixpanelTrackEvent } from "services/mixpanel";
import { Avatar } from "subframe/components/Avatar";
import { CustomBadge } from "subframe/components/CustomBadge";
import { CustomStatCard } from "subframe/components/CustomStatCard";
import { InboxHeader } from "subframe/components/InboxHeader";
import { InboxRow } from "subframe/components/InboxRow";
import { PageHeader } from "subframe/components/PageHeader";
import { Select } from "subframe/components/Select";
import { TabsPills } from "subframe/components/TabsPills";
import { isEmpty as isArrayEmpty, sortArrayObjects } from "utils/array";
import { getLastNDaysFromToday, getRelativeDate } from "utils/date";
import { subscribe, unsubscribe } from "utils/event";
import { isNumber } from "utils/number";
import { isEmpty as isObjectEmpty } from "utils/object";
import {
  getAcronym,
  getFullName,
  isEmpty as isStringEmpty,
} from "utils/string";
import EmployeeProfileSlideout from "../EmployeeProfileSlideout";
import EmptyState from "../EmptyState";

function Inbox() {
  const defaultSort = SORT_BY_OPTIONS["Oldest to Newest"];
  const defaultInboxDateFilter = INBOX_DATE_FILTER_OPTIONS["Last 7 days"];

  const navigate = useNavigate();

  const [inboxDateFilter, setInboxDateFilter] = useState({
    endDate:
      getLastNDaysFromToday(defaultInboxDateFilter.value)?.endDate || null,
    startDate:
      getLastNDaysFromToday(defaultInboxDateFilter.value)?.startDate || null,
  });
  const [atRisks, setAtRisks] = useState({});
  const [resolved, setResolved] = useState([]);
  const [employee, setEmployee] = useState({});
  const [dismissed, setDismissed] = useState([]);
  const [inboxSummary, setInboxSummary] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTab, setSelectedTab] = useState("At-risk");
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [selectedSort, setSelectedSort] = useState(defaultSort);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState();
  const [selectedEscalationId, setSelectedEscalationId] = useState();
  const [isLoadingAtRisks, setIsLoadingAtRisks] = useState(false);
  const [isLoadingResolved, setIsLoadingResolved] = useState(false);
  const [isFetchingEmployeeHistory, setIsFetchingEmployeeHistory] =
    useState(false);
  const [isFetchingEmployee, setIsFetchingEmployee] = useState(false);
  const [isLoadingDismissed, setIsLoadingDismissed] = useState(false);
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);
  const [selectedInboxDateFilter, setSelectedInboxDateFilter] = useState(
    defaultInboxDateFilter.label
  );
  const [isProfileSlideoutOpen, setIsProfileSlideoutOpen] = useState(false);
  const [isInboxSummaryLoading, setIsInboxSummaryLoading] = useState(false);

  const { user, isLoading: isLoadingUser } = useUser();

  useEffect(() => {
    subscribe("closeSlideout", handleCloseProfileSlideout);

    mixpanelTrackEvent(EVENT_TYPES.INBOX_PAGE_NAVIGATION);

    return () => {
      unsubscribe("closeSlideout", () => {});
    };
  }, []);

  useEffect(() => {
    handleFetchInitialData();
  }, [inboxDateFilter]);

  useEffect(() => {
    setAtRisks({
      overdue: sortArrayObjects(
        atRisks.overdue,
        "escalated_on",
        selectedSort.value
      ),
      new: sortArrayObjects(atRisks.new, "escalated_on", selectedSort.value),
    });
    setResolved(sortArrayObjects(resolved, "resolved_on", selectedSort.value));
    setDismissed(
      sortArrayObjects(dismissed, "dismissed_on", selectedSort.value)
    );
  }, [selectedSort]);

  const handleFetchInitialData = () => {
    fetchAndSetInboxSummary(inboxDateFilter);
    handleFetchAtRisks({
      ...inboxDateFilter,
      status: INBOX_ESCALATION_TYPE["AT_RISK"],
    });
    handleFetchResolved({
      ...inboxDateFilter,
      status: INBOX_ESCALATION_TYPE["RESOLVED"],
    });
    handleFetchDismissed({
      ...inboxDateFilter,
      status: INBOX_ESCALATION_TYPE["DISMISSED"],
    });
  };

  const handleEscalationFollowUpSubmit = () => {
    handleFetchInitialData();
    handleProfileSlideoutDataFetch(selectedEmployeeId, selectedEscalationId);
  };

  const handleRecognitionPostSubmit = () => {
    handleFetchInitialData();
    handleProfileSlideoutDataFetch(selectedEmployeeId, selectedEscalationId);
  };

  const handleSendReferralSMSSubmit = () => {
    handleFetchInitialData();
    handleProfileSlideoutDataFetch(selectedEmployeeId, selectedEscalationId);
  };

  const fetchAndSetInboxSummary = async (filter = {}) => {
    setIsInboxSummaryLoading(true);
    const response = await fetchAndSetInboxSummaryAsync(filter);

    setIsInboxSummaryLoading(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching Inbox Summary: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);
      setInboxSummary({});
    }

    setInboxSummary(response?.result?.escalation_stats);
  };

  const fetchAndSetInboxSummaryAsync = async (filter = {}) => {
    try {
      return await fetchInboxSummary({
        start_date: filter?.startDate,
        end_date: filter?.endDate,
      });
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Method to fetch at escalations by API call.
   *
   * @param {Object} filter
   * @returns {Promise}
   */
  const fetchEscalationsAsync = async (filter = {}) => {
    try {
      return await fetchEscalations({
        start_date: filter?.startDate,
        end_date: filter?.endDate,
        status: filter?.status,
      });
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Method to handle the fetching of the at risk employees.
   *
   * @param {Object} filter
   * @returns {Null | Undefined}
   */
  const handleFetchAtRisks = async (filter = {}) => {
    setIsLoadingAtRisks(true);
    const response = await fetchEscalationsAsync(filter);

    setIsLoadingAtRisks(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching At Risks: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);
      setAtRisks({});

      return;
    }

    setAtRisks({
      overdue: sortArrayObjects(
        response?.result?.data?.overdue,
        "escalated_on",
        selectedSort.value
      ),
      new: sortArrayObjects(
        response?.result?.data?.new,
        "escalated_on",
        selectedSort.value
      ),
    });
  };

  /**
   * Method to handle the fetching of the resolved employee escalation.
   *
   * @param {Object} filter
   * @returns {Null | Undefined}
   */
  const handleFetchResolved = async (filter = {}) => {
    setIsLoadingResolved(true);
    const response = await fetchEscalationsAsync(filter);

    setIsLoadingResolved(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching Resolved: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);
      setResolved([]);

      return;
    }

    setResolved(
      sortArrayObjects(
        response?.result?.data?.escalations,
        "resolved_on",
        selectedSort.value
      )
    );
  };

  /**
   * Method to handle the fetching of the dismissed employee escalation.
   *
   * @param {Object} filter
   * @returns {Null | Undefined}
   */
  const handleFetchDismissed = async (filter = {}) => {
    setIsLoadingDismissed(true);
    const response = await fetchEscalationsAsync(filter);

    setIsLoadingDismissed(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching Dismissed: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);
      setDismissed([]);

      return;
    }

    setDismissed(
      sortArrayObjects(
        response?.result?.data?.escalations,
        "dismissed_on",
        selectedSort.value
      )
    );
  };

  const handleGetLastNDaysFromToday = (n = 0) => {
    const result = getLastNDaysFromToday(n);
    const startDate =
      dayjs(result?.startDate)?.format("YYYY-MM-DD") === "Invalid Date"
        ? null
        : dayjs(result?.startDate)?.format("YYYY-MM-DD");
    const endDate =
      dayjs(result?.endDate)?.format("YYYY-MM-DD") === "Invalid Date"
        ? null
        : dayjs(result?.endDate)?.format("YYYY-MM-DD");

    setInboxDateFilter({
      startDate,
      endDate,
    });
  };

  const handleEscalationClick = (employeeId, escalationId) => {
    setSelectedEmployeeId(employeeId);
    setSelectedEscalationId(escalationId);
    openProfileSlideout();
    handleProfileSlideoutDataFetch(employeeId, escalationId);

    mixpanelTrackEvent(EVENT_TYPES.ESCALATED_EMPLOYEE_CLICK, {
      employeeId: employeeId,
    });
  };

  const openProfileSlideout = () => {
    setIsProfileSlideoutOpen(true);
  };

  const closeProfileSlideout = () => {
    setIsProfileSlideoutOpen(false);
  };

  const handleCloseProfileSlideout = () => {
    setSelectedEmployeeId();
    setSelectedEscalationId();
    setEmployee({});
    setEmployeeHistory([]);
    closeProfileSlideout();
  };

  const handleProfileSlideoutDataFetch = (employeeId, escalationId) => {
    handleFetchEmployee(employeeId, escalationId);
    handleFetchEmployeeHistory(employeeId);
  };

  /**
   * Handles fetching of the employee profile.
   *
   * @param {Number} employeeId
   * @param {Number} escalationId
   * @param {Boolean} isSilent
   *
   * @returns {Promise}
   */
  const handleFetchEmployee = async (
    employeeId,
    escalationId,
    isSilent = false
  ) => {
    !isSilent && setIsFetchingEmployee(true);
    const response = await fetchEmployeeAsync(employeeId, escalationId);

    !isSilent && setIsFetchingEmployee(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching employee details: ${response?.error?.message}`
      );
      setEmployee({});
      setIsErrorMessageShown(true);

      return;
    }

    if (!isObjectEmpty(response?.result?.data)) {
      setEmployee(response?.result?.data);

      return;
    }
  };

  /**
   * Handles fetching of the employee's history.
   *
   * @param {Number} employeeId
   * @param {Object|null} injectedData
   * @param {Boolean} isSilent
   *
   * @returns {Promise}
   */
  const handleFetchEmployeeHistory = async (
    employeeId,
    injectedData = null,
    isSilent = false
  ) => {
    !isSilent && setIsFetchingEmployeeHistory(true);
    const response = await fetchEmployeeHistoryAsync(employeeId);

    !isSilent && setIsFetchingEmployeeHistory(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching employee's history: ${response?.error?.message}`
      );
      setEmployeeHistory([]);
      setIsErrorMessageShown(true);

      return;
    }

    if (!isArrayEmpty(response?.result?.history)) {
      setEmployeeHistory([
        ...response?.result?.history,
        ...(!isObjectEmpty(injectedData) ? [injectedData] : []),
      ]);

      return;
    }
  };

  /**
   * Method to fetch the employee profile.
   *
   * @param {Number} employeeId
   */
  const fetchEmployeeAsync = async (employeeId, escalationId) => {
    try {
      return await fetchEmployee({
        employee_id: employeeId,
        escalation_id: escalationId,
      });
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Method to fetch the employee's escalation history.
   *
   * @param {Number} employeeId
   * @param {Number} escalationId
   */
  const fetchEmployeeHistoryAsync = async (employeeId) => {
    try {
      return await fetchEmployeeHistory({
        employee_id: employeeId,
      });
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/");
      }

      return { hasError: true, error };
    }
  };

  const renderAtRisks = () => {
    return (
      <>
        <div className="w-full flex-col items-start flex">
          <InboxHeader>
            <span className="w-full text-default-font text-subheader font-subheader">
              Overdue
            </span>
          </InboxHeader>
          <div className="w-full flex-col items-start flex max-h-[340px] overflow-auto">
            {isLoadingAtRisks ? (
              <div className="gap-2 w-full flex-col flex">
                <Skeleton variant="rounded" width="100%" height="50px" />
                <Skeleton variant="rounded" width="100%" height="50px" />
                <Skeleton variant="rounded" width="100%" height="50px" />
              </div>
            ) : isArrayEmpty(atRisks?.overdue) ? (
              <div className="h-48 w-full">
                <EmptyState
                  icon=<SubframeCore.Icon
                    className="text-[44px] font-[100] leading-[44px] text-neutral-400"
                    name="FeatherFileCheck"
                  />
                  primaryText="No overdue escalations yet."
                  secondaryText="Great job! You’re all done following-up with at-risk employees."
                />
              </div>
            ) : (
              atRisks.overdue.map((atRisk) => {
                return (
                  <InboxRow
                    onClick={() => {
                      handleEscalationClick(
                        atRisk?.employee_id,
                        atRisk?.escalation_id
                      );
                    }}
                  >
                    <Avatar
                      variant={
                        initializeSentiment(atRisk.sentiment_score)
                          .avatarVariant
                      }
                    >
                      {getAcronym(
                        getFullName(atRisk.first_name, atRisk.last_name)
                      )}
                    </Avatar>
                    <div className="grow shrink-0 basis-0 w-full flex-col items-start flex">
                      <span className="text-default-font text-body-bold font-body-bold">
                        {isStringEmpty(
                          getFullName(atRisk.first_name, atRisk.last_name)
                        )
                          ? "-"
                          : getFullName(atRisk.first_name, atRisk.last_name)}
                      </span>
                      <span className="text-subtext-color text-label font-label">
                        {isStringEmpty(atRisk.job_title)
                          ? "-"
                          : atRisk.job_title}
                        , {isStringEmpty(atRisk.region) ? "-" : atRisk.region}
                      </span>
                    </div>
                    <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                      {renderSentimentBadgeWithScore(
                        atRisk.sentiment_score,
                        atRisk.response_rate,
                        true
                      )}
                    </div>
                    <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                      <CustomBadge variant="neutral">
                        {isStringEmpty(atRisk.survey_titles[0])
                          ? "-"
                          : atRisk.survey_titles[0]}
                        :{" "}
                        {isObjectEmpty(
                          ESCALATION_REASONS[atRisk.escalation_reason]
                        )
                          ? "-"
                          : ESCALATION_REASONS[atRisk.escalation_reason].label}
                      </CustomBadge>
                    </div>
                    <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                      <div className="gap-2 pt-1 pr-1 pb-1 pl-1 items-start items-center flex">
                        <SubframeCore.Icon
                          className="text-default-font text-body-bold font-body-bold"
                          name="FeatherCalendar"
                        />
                        <span className="flex items-center text-default-font text-label font-label first-letter:uppercase">
                          {getRelativeDate(atRisk.escalated_on)}
                        </span>
                      </div>
                    </div>
                  </InboxRow>
                );
              })
            )}
          </div>
        </div>
        <div className="w-full flex-col items-start flex">
          <InboxHeader>
            <span className="w-full text-default-font text-subheader font-subheader">
              New
            </span>
          </InboxHeader>
          <div className="w-full flex-col items-start flex max-h-[340px] overflow-auto">
            {isLoadingAtRisks ? (
              <div className="gap-2 w-full flex-col flex">
                <Skeleton variant="rounded" width="100%" height="50px" />
                <Skeleton variant="rounded" width="100%" height="50px" />
                <Skeleton variant="rounded" width="100%" height="50px" />
              </div>
            ) : isArrayEmpty(atRisks?.new) ? (
              <div className="h-48 w-full">
                <EmptyState
                  icon=<SubframeCore.Icon
                    className="text-[44px] font-[100] leading-[44px] text-neutral-400"
                    name="FeatherFileCheck"
                  />
                  primaryText="No new escalations yet."
                  secondaryText="Great job! You’re all done following-up with at-risk employees."
                />
              </div>
            ) : (
              atRisks.new.map((atRisk) => {
                return (
                  <InboxRow
                    onClick={() => {
                      handleEscalationClick(
                        atRisk?.employee_id,
                        atRisk?.escalation_id
                      );
                    }}
                  >
                    <Avatar
                      variant={
                        initializeSentiment(atRisk.sentiment_score)
                          .avatarVariant
                      }
                    >
                      {getAcronym(
                        getFullName(atRisk.first_name, atRisk.last_name)
                      )}
                    </Avatar>
                    <div className="grow shrink-0 basis-0 w-full flex-col items-start flex">
                      <span className="text-default-font text-body-bold font-body-bold">
                        {isStringEmpty(
                          getFullName(atRisk.first_name, atRisk.last_name)
                        )
                          ? "-"
                          : getFullName(atRisk.first_name, atRisk.last_name)}
                      </span>
                      <span className="text-subtext-color text-label font-label">
                        {isStringEmpty(atRisk.job_title)
                          ? "-"
                          : atRisk.job_title}
                        , {isStringEmpty(atRisk.region) ? "-" : atRisk.region}
                      </span>
                    </div>
                    <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                      {renderSentimentBadgeWithScore(
                        atRisk.sentiment_score,
                        atRisk.response_rate,
                        true
                      )}
                    </div>
                    <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                      <CustomBadge variant="neutral">
                        {isStringEmpty(atRisk.survey_titles[0])
                          ? "-"
                          : atRisk.survey_titles[0]}
                        :{" "}
                        {isObjectEmpty(
                          ESCALATION_REASONS[atRisk.escalation_reason]
                        )
                          ? "-"
                          : ESCALATION_REASONS[atRisk.escalation_reason].label}
                      </CustomBadge>
                    </div>
                    <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                      <div className="gap-2 pt-1 pr-1 pb-1 pl-1 items-start items-center flex">
                        <SubframeCore.Icon
                          className="text-default-font text-body-bold font-body-bold"
                          name="FeatherCalendar"
                        />
                        <span className="flex items-center text-default-font text-label font-label first-letter:uppercase">
                          {getRelativeDate(atRisk.escalated_on)}
                        </span>
                      </div>
                    </div>
                  </InboxRow>
                );
              })
            )}
          </div>
        </div>
      </>
    );
  };

  const renderResolved = () => {
    return (
      <>
        <div className="w-full flex-col items-start flex flex-1 overflow-auto">
          {isLoadingResolved ? (
            <div className="gap-2 w-full flex-col flex">
              <Skeleton variant="rounded" width="100%" height="50px" />
              <Skeleton variant="rounded" width="100%" height="50px" />
              <Skeleton variant="rounded" width="100%" height="50px" />
            </div>
          ) : isArrayEmpty(resolved) ? (
            <EmptyState
              icon=<SubframeCore.Icon
                className="text-[44px] font-[100] leading-[44px] text-neutral-400"
                name="FeatherFileBox"
              />
              primaryText="No resolved escalations yet."
              secondaryText="Follow-up with at-risk employees."
            />
          ) : (
            resolved.map((item) => {
              return (
                <InboxRow
                  onClick={() => {
                    handleEscalationClick(
                      item?.employee_id,
                      item?.escalation_id
                    );
                  }}
                >
                  <Avatar
                    variant={
                      initializeSentiment(item.sentiment_score).avatarVariant
                    }
                  >
                    {getAcronym(getFullName(item.first_name, item.last_name))}
                  </Avatar>
                  <div className="grow shrink-0 basis-0 w-full flex-col items-start flex">
                    <span className="text-default-font text-body-bold font-body-bold">
                      {isStringEmpty(
                        getFullName(item.first_name, item.last_name)
                      )
                        ? "-"
                        : getFullName(item.first_name, item.last_name)}
                    </span>
                    <span className="text-subtext-color text-label font-label">
                      {isStringEmpty(item.job_title) ? "-" : item.job_title},{" "}
                      {isStringEmpty(item.region) ? "-" : item.region}
                    </span>
                  </div>
                  <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                    {renderSentimentBadgeWithScore(
                      item.sentiment_score,
                      item.response_rate,
                      true
                    )}
                  </div>
                  <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                    <CustomBadge variant="neutral">
                      {isStringEmpty(item.survey_titles[0])
                        ? "-"
                        : item.survey_titles[0]}
                      :{" "}
                      {isObjectEmpty(ESCALATION_REASONS[item.escalation_reason])
                        ? "-"
                        : ESCALATION_REASONS[item.escalation_reason].label}
                    </CustomBadge>
                  </div>
                  <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                    <div className="gap-2 pt-1 pr-1 pb-1 pl-1 items-start items-center flex">
                      <SubframeCore.Icon
                        className="text-default-font text-body-bold font-body-bold"
                        name="FeatherCalendar"
                      />
                      <span className="flex items-center text-default-font text-label font-label first-letter:uppercase">
                        {getRelativeDate(item.resolved_on)}
                      </span>
                    </div>
                  </div>
                </InboxRow>
              );
            })
          )}
        </div>
      </>
    );
  };

  const renderDismissed = () => {
    return (
      <>
        <div className="w-full flex-col items-start flex flex-1 overflow-auto">
          {isLoadingDismissed ? (
            <div className="gap-2 w-full flex-col flex">
              <Skeleton variant="rounded" width="100%" height="50px" />
              <Skeleton variant="rounded" width="100%" height="50px" />
              <Skeleton variant="rounded" width="100%" height="50px" />
            </div>
          ) : isArrayEmpty(dismissed) ? (
            <EmptyState
              icon=<SubframeCore.Icon
                className="text-[44px] font-[100] leading-[44px] text-neutral-400"
                name="FeatherFileBox"
              />
              primaryText="No dismissed escalations yet."
              secondaryText="Follow-up with at-risk employees."
            />
          ) : (
            dismissed.map((item) => {
              return (
                <InboxRow
                  onClick={() => {
                    handleEscalationClick(
                      item?.employee_id,
                      item?.escalation_id
                    );
                  }}
                >
                  <Avatar
                    variant={
                      initializeSentiment(item.sentiment_score).avatarVariant
                    }
                  >
                    {getAcronym(getFullName(item.first_name, item.last_name))}
                  </Avatar>
                  <div className="grow shrink-0 basis-0 w-full flex-col items-start flex">
                    <span className="text-default-font text-body-bold font-body-bold">
                      {isStringEmpty(
                        getFullName(item.first_name, item.last_name)
                      )
                        ? "-"
                        : getFullName(item.first_name, item.last_name)}
                    </span>
                    <span className="text-subtext-color text-label font-label">
                      {isStringEmpty(item.job_title) ? "-" : item.job_title},{" "}
                      {isStringEmpty(item.region) ? "-" : item.region}
                    </span>
                  </div>
                  <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                    {renderSentimentBadgeWithScore(
                      item.sentiment_score,
                      item.response_rate,
                      true
                    )}
                  </div>
                  <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                    <CustomBadge variant="neutral">
                      {isStringEmpty(item.survey_titles[0])
                        ? "-"
                        : item.survey_titles[0]}
                      :{" "}
                      {isObjectEmpty(ESCALATION_REASONS[item.escalation_reason])
                        ? "-"
                        : ESCALATION_REASONS[item.escalation_reason].label}
                    </CustomBadge>
                  </div>
                  <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-2 items-start justify-center flex">
                    <div className="gap-2 pt-1 pr-1 pb-1 pl-1 items-start items-center flex">
                      <SubframeCore.Icon
                        className="text-default-font text-body-bold font-body-bold"
                        name="FeatherCalendar"
                      />
                      <span className="flex items-center text-default-font text-label font-label first-letter:uppercase">
                        {getRelativeDate(item.dismissed_on)}
                      </span>
                    </div>
                  </div>
                </InboxRow>
              );
            })
          )}
        </div>
      </>
    );
  };

  const handleRenderLineItems = () => {
    if (selectedTab === "At-risk") {
      return renderAtRisks();
    }

    if (selectedTab === "Resolved") {
      return renderResolved();
    }

    if (selectedTab === "Dismissed") {
      return renderDismissed();
    }
  };

  /**
   * Renders the error snackbar.
   *
   * @returns {Component}
   */
  const renderErrorSnackbar = () => (
    <Snackbar
      open={isErrorMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsErrorMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsErrorMessageShown(false)}
        severity="error"
        sx={{ width: "100%" }}
      >
        {errorMessage}
      </MuiAlert>
    </Snackbar>
  );

  return (
    <>
      <EmployeeProfileSlideout
        employee={employee}
        user={user}
        isLoadingUser={isLoadingUser}
        close={handleCloseProfileSlideout}
        isOpen={isProfileSlideoutOpen}
        isFetchingEmployee={isFetchingEmployee}
        employeeHistory={employeeHistory}
        isFetchingEmployeeHistory={isFetchingEmployeeHistory}
        employeeId={selectedEmployeeId}
        escalationId={selectedEscalationId}
        onEscalationFollowUpSubmit={handleEscalationFollowUpSubmit}
        onRecognitionPostSubmit={handleRecognitionPostSubmit}
        onSendReferralSMSSubmit={handleSendReferralSMSSubmit}
      />
      <div className="w-full h-full flex-col gap-8 pt-12 pr-16 pb-12 pl-16 items-start flex bg-default-background overflow-auto">
        <PageHeader>
          <div className="grow shrink-0 basis-0 w-full gap-4 items-center flex">
            <SubframeCore.Icon
              className="text-neutral-400 text-header font-header"
              name="FeatherInbox"
            />
            <span className="grow shrink-0 basis-0 w-full text-default-font text-section-header font-section-header line-clamp-1">
              Inbox
            </span>
          </div>
          <Select
            className="flex-none w-auto h-9"
            placeholder="Last 7 days"
            size="Small"
            value={selectedInboxDateFilter}
            onValueChange={(label) => {
              setSelectedInboxDateFilter(label);
              handleGetLastNDaysFromToday(
                INBOX_DATE_FILTER_OPTIONS[label].value
              );
            }}
          >
            {Object.keys(INBOX_DATE_FILTER_OPTIONS).map((key) => (
              <Select.Item
                value={INBOX_DATE_FILTER_OPTIONS[key].label}
                key={INBOX_DATE_FILTER_OPTIONS[key].value}
              />
            ))}
          </Select>
        </PageHeader>
        <div className="w-full gap-4 items-center flex">
          {isInboxSummaryLoading ? (
            <>
              <Skeleton variant="rounded" width="100%" height="85px" />
              <Skeleton variant="rounded" width="100%" height="85px" />
              <Skeleton variant="rounded" width="100%" height="85px" />
            </>
          ) : (
            <>
              <CustomStatCard
                title="At-risk"
                value={
                  !isNumber(inboxSummary?.active_count)
                    ? "-"
                    : inboxSummary?.active_count?.toString()
                }
                variant="at-risk"
                onClick={() => setSelectedTab("At-risk")}
                className="hover:shadow-md transition-shadow hover:cursor-pointer"
              />
              <CustomStatCard
                title="Resolved"
                value={
                  !isNumber(inboxSummary?.resolved_count)
                    ? "-"
                    : inboxSummary?.resolved_count?.toString()
                }
                variant="resolved"
                onClick={() => setSelectedTab("Resolved")}
                className="hover:shadow-md transition-shadow hover:cursor-pointer"
              />
              <CustomStatCard
                title="Dismissed"
                value={
                  !isNumber(inboxSummary?.dismiss_count)
                    ? "-"
                    : inboxSummary?.dismiss_count?.toString()
                }
                onClick={() => setSelectedTab("Dismissed")}
                className="hover:shadow-md transition-shadow hover:cursor-pointer"
              />
            </>
          )}
        </div>
        <div className="w-full justify-between flex-wrap items-start flex">
          <TabsPills className="flex-none w-auto h-auto">
            {INBOX_TABS.map((tab) => (
              <TabsPills.Item
                active={selectedTab === tab}
                value={tab}
                onClick={() => setSelectedTab(tab)}
              >
                {tab}
              </TabsPills.Item>
            ))}
          </TabsPills>
          <div className="gap-2 items-center flex">
            <Select
              className="flex-none w-auto h-9"
              placeholder="Sort by date"
              size="Small"
              defaultValue={defaultSort.label}
              onValueChange={(value) => setSelectedSort(SORT_BY_OPTIONS[value])}
            >
              {Object.keys(SORT_BY_OPTIONS).map((key) => {
                return <Select.Item value={SORT_BY_OPTIONS[key].label} />;
              })}
            </Select>
          </div>
        </div>
        {handleRenderLineItems()}
      </div>
      {renderErrorSnackbar()}
    </>
  );
}

export default Inbox;
